// src/api/executedContractsApi.ts

import { ChatStreamCallbacks } from "../interfaces/chatTypes";
import { ChatApiError } from "./chatApi";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { parseErrorResponse } from "./utils/errorHandler";
import { normalizeAccessToken } from "../utils/typeHelpers";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { routeChunkToCallback, ChunkRouterState } from "./utils/chunkRouter";
import { streamErrorToPostHog, ApiNames, ErrorCodes } from "./utils/captureError";
const log = createLogger("ExecutedContractsApi");

// Use the same base URL pattern as other APIs
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * API interface specifically for executed contracts endpoints
 *
 * This class handles the unique requirements of the contract-qa agent,
 * including support for additional template files and different parameter names.
 *
 * It mirrors the structure of chatApi.ts but with contract-specific adaptations.
 */
export class ExecutedContractsApi {
  private abortController: AbortController | null = null;
  private routerState: ChunkRouterState = {};

  /**
   * Send a streaming chat request to the executed contracts endpoint
   *
   * This method handles the specific parameter naming and additional file support
   * required by the contract-qa agent's API.
   */
  async sendStreamingChatRequest(
    request: {
      message: string;
      executedAgreementFile: File; // Main document (currently open in Word)
      additionalFiles?: File[]; // Template files for comparison (supports multiple)
      selectedTextFile?: File; // Selected text from document
      id?: string; // Session ID
      docId?: string; // Document ID for caching
      bypassCache?: boolean;
      details?: any;
      chatHistory?: any[];
      accessToken?: string; // Optional access token for authentication
    },
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Log the request details to debug web search
    log.info("🔍 EXECUTED CONTRACTS API REQUEST:", {
      hasDetails: !!request.details,
      webSearchFlag: request.details?.web_search,
      details: request.details,
    });

    // Create abort controller for cancellation support
    this.abortController = new AbortController();
    // Reset router state for new streaming session
    this.routerState = {};

    try {
      // Notify that streaming has started
      callbacks.onStart();

      // Build form data with contract-specific parameters
      const formData = this.buildFormData(request);

      log.info("Sending executed contracts chat request", {
        additionalFileCount: request.additionalFiles?.length || 0,
        sessionId: request.id,
        messagePreview: request.message.substring(0, 50) + "...",
      });

      // TEMPORARY DEBUG: Log the contract-qa API call
      const endpoint = `${API_BASE_URL}/chat/executed_contracts/stream/`;
      log.info("📤 MAKING CONTRACT Q&A API CALL:", {
        method: "POST",
        endpoint: endpoint,
        formDataFields: (() => {
          const keys: string[] = [];
          formData.forEach((_, key) => keys.push(key));
          return keys;
        })(),
        hasExecutedAgreementFile: formData.has("executed_agreement_file"),
        additionalFileCount: request.additionalFiles?.length || 0,
        additionalFileNames: request.additionalFiles?.map((f) => f.name) || [],
        hasSelectedTextFile: formData.has("selected_text_file"),
        sessionId: formData.get("id"),
        accessToken: request.accessToken,
      });

      const api_request = constructRequest(
        endpoint,
        "POST",
        normalizeAccessToken(request.accessToken),
        formData,
        this.abortController.signal
      );

      // Make the HTTP request to the executed contracts endpoint
      const response = await fetch(api_request);

      // Handle errors through the shared parser: it also runs the 401 refresh
      // bridge, so an expired token triggers a token refresh instead of every
      // subsequent request failing with the same stale token.
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new ChatApiError(
          errorData.message,
          errorData.error_code,
          errorData.status_code,
          errorData.request_id
        );
      }

      // Process the streaming response (same format as red-line-analyzer)
      await this.processStreamingResponse(response, callbacks);

      // Notify completion
      callbacks.onComplete();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        // Request was cancelled - don't call error callback
        return;
      }

      if (error instanceof ChatApiError) {
        streamErrorToPostHog(error, ApiNames.EXECUTED_CONTRACTS_API, undefined, {
          chat_id: request.id,
        });
        callbacks.onError(error);
      } else {
        // Handle client-side errors with helpful, contextual messages
        const clientError = createClientErrorInfo(error, "streaming");
        const clientApiError = new ChatApiError(clientError.message, clientError.errorCode, 0);
        // Also capture the ORIGINAL error: the wrapper only carries the
        // user-facing message and the CONNECTION_FAILED code, so
        // without this we never see the underlying message (e.g. "Failed to
        // fetch"). chat_id joins this to the BE "Stream outcome" log.
        streamErrorToPostHog(clientApiError, ApiNames.EXECUTED_CONTRACTS_API, undefined, {
          chat_id: request.id,
          original_error_name: error instanceof Error ? error.name : undefined,
          original_error_message: error instanceof Error ? error.message : String(error),
        });
        callbacks.onError(clientApiError);
      }
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Send a streaming summary request to the executed contracts endpoint
   *
   * This handles the summary-specific requirements for contract analysis.
   *
   * @deprecated RETIRED with the Analysis tab. Its only caller is the
   * deprecated agentAwareApiAdapter summary chain, and the
   * `/summary/executed_contracts/stream/` route it targets is no longer
   * mounted on the backend, so a call would only 404. Note the REST of this
   * class is live - `sendStreamingChatRequest` serves the executed-contracts
   * chat path.
   */
  async sendStreamingSummaryRequest(
    request: {
      executedAgreementFile: File;
      additionalFiles?: File[];
      id?: string;
      bypassCache?: boolean;
      details?: any;
      docId?: string;
      accessToken?: string;
    },
    callbacks: {
      onStart: () => void;
      onChunk: (chunk: string) => void;
      onComplete: () => void;
      onError: (error: Error) => void;
    }
  ): Promise<AbortController> {
    const abortController = new AbortController();

    // Start the async operation
    (async () => {
      try {
        const formData = new FormData();
        formData.append("executed_agreement_file", request.executedAgreementFile);

        if (request.additionalFiles && request.additionalFiles.length > 0) {
          request.additionalFiles.forEach((file: File) => {
            formData.append("additional_files", file);
          });
        }

        if (request.details) {
          formData.append("details", JSON.stringify(request.details));
        }

        if (request.docId) {
          formData.append("doc_id", request.docId);
        }

        // Build URL with query parameters
        let url = `${API_BASE_URL}/summary/executed_contracts/stream/`;
        const queryParams = new URLSearchParams();

        if (request.id) queryParams.append("id", request.id);
        if (request.bypassCache !== undefined) {
          queryParams.append("bypass_cache", request.bypassCache.toString());
        }

        const queryString = queryParams.toString();
        if (queryString) {
          url += "?" + queryString;
        }

        log.info("Sending executed contracts summary request", {
          additionalFileCount: request.additionalFiles?.length || 0,
          sessionId: request.id,
        });

        // TEMPORARY DEBUG: Log the executed contracts summary API call
        log.debug("📤 MAKING CONTRACT Q&A SUMMARY API CALL:", {
          method: "POST",
          endpoint: url,
          formDataFields: (() => {
            const keys: string[] = [];
            formData.forEach((_, key) => keys.push(key));
            return keys;
          })(),
          hasExecutedAgreementFile: formData.has("executed_agreement_file"),
          additionalFileCount: request.additionalFiles?.length || 0,
          additionalFileNames: request.additionalFiles?.map((f) => f.name) || [],
          sessionId: request.id,
          queryString: queryString,
          access_token: request.accessToken,
        });

        callbacks.onStart();
        const api_request = constructRequest(
          url,
          "POST",
          normalizeAccessToken(request.accessToken),
          formData,
          abortController.signal
        );
        const response = await fetch(api_request);

        if (!response.ok) {
          const errorData = await parseErrorResponse(response);
          throw new ChatApiError(
            errorData.message,
            errorData.error_code,
            errorData.status_code,
            errorData.request_id
          );
        }

        // Process streaming response
        await this.processSummaryStream(response, callbacks);

        callbacks.onComplete();
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const responseError = error instanceof Error ? error : new Error("Unknown error occurred");
        streamErrorToPostHog(responseError, ApiNames.EXECUTED_CONTRACTS_API);
        callbacks.onError(responseError);
      }
    })();

    return abortController;
  }

  /**
   * Cancel ongoing request
   */
  cancelRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Build FormData for chat requests with contract-specific field names
   */
  private buildFormData(request: any): FormData {
    const formData = new FormData();

    // Required fields with contract-specific names
    formData.append("message", request.message);
    formData.append("executed_agreement_file", request.executedAgreementFile);

    // Optional files - support multiple additional files
    if (request.additionalFiles && request.additionalFiles.length > 0) {
      request.additionalFiles.forEach((file: File) => {
        formData.append("additional_files", file);
      });

      log.debug("Appending additional files", {
        fileCount: request.additionalFiles.length,
        fileNames: request.additionalFiles.map((f: File) => f.name),
      });
    }

    if (request.selectedTextFile) {
      formData.append("selected_text_file", request.selectedTextFile);
    }

    // Session and configuration parameters
    if (request.id) {
      formData.append("id", request.id);
    }

    if (request.docId) {
      formData.append("doc_id", request.docId);
    }

    if (request.bypassCache !== undefined) {
      formData.append("bypass_cache", request.bypassCache.toString());
    }

    if (request.details) {
      // Log the details being sent for debugging the new features
      log.debug("Sending enhanced details configuration", {
        web_search: request.details.web_search,
        thinking: request.details.thinking,
        benchmark_mode: request.details.benchmark_mode,
      });

      formData.append("details", JSON.stringify(request.details));
    }

    if (request.chatHistory && request.chatHistory.length > 0) {
      formData.append("chat_history", JSON.stringify(request.chatHistory));
    }

    return formData;
  }

  /**
   * Process streaming response for chat
   *
   * Since the response format is identical to red-line-analyzer,
   * we can reuse the same processing logic.
   */
  private async processStreamingResponse(
    response: Response,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // The implementation is identical to chatApi.ts processStreamingResponse
    // since both APIs use the same SSE format

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Could not get response reader for streaming");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        buffer = this.processBufferedLines(buffer, callbacks);
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Process buffered lines from SSE stream
   *
   * This is identical to the chat API implementation since the format is the same.
   */
  private processBufferedLines(buffer: string, callbacks: ChatStreamCallbacks): string {
    const lines = buffer.split("\n");
    const remainingBuffer = lines.pop() || "";

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      if (trimmedLine.startsWith("data:")) {
        const jsonData = trimmedLine.substring(5).trim();

        if (jsonData === "[DONE]") {
          break;
        }

        try {
          const chunk = JSON.parse(jsonData);
          // Route to appropriate callback based on chunk type
          this.routeChunkToCallback(chunk, callbacks);
        } catch (error) {
          log.error("Failed to parse streaming chunk", {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    }

    return remainingBuffer;
  }

  /**
   * Route chunks to appropriate callbacks
   *
   * Since the chunk format is identical between APIs, we use the same routing logic.
   */
  private routeChunkToCallback(chunk: any, callbacks: ChatStreamCallbacks): void {
    // Use shared chunk router with standard configuration and persistent state
    routeChunkToCallback(chunk, callbacks, log, {
      verboseAutoRedlineLogging: true,
      verboseWebSearchLogging: true,
      enhancedErrorHandling: true,
      enableFallbackToText: true,
      state: this.routerState,
    });
  }

  /**
   * Process summary streaming response
   *
   * @deprecated RETIRED - only reachable from sendStreamingSummaryRequest above.
   */
  private async processSummaryStream(response: Response, callbacks: any): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Could not get response reader");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim().startsWith("data: ")) {
            const data = line.substring(6).trim();

            if (data === "[DONE]") break;

            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                callbacks.onChunk(parsed.text);
              }
            } catch (e) {
              log.error("Error parsing summary chunk", { error: e });
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

// Export singleton instance
export const executedContractsApi = new ExecutedContractsApi();
